


import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { createUserWithEmailAndPassword, updateProfile, sendPasswordResetEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, collection, getDocs, setDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Users, UserPlus, LogOut, Edit3, Save, X, Check, AlertCircle, Menu, Search, Key, Trash2 } from 'lucide-react';

function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'employee', name: '', salary: '', joiningDate: '' });
  const [activeTab, setActiveTab] = useState('users');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [adminCredentials, setAdminCredentials] = useState({ email: '', password: '' });
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [isAdminEmailSet, setIsAdminEmailSet] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (isCreatingUser) {
        return;
      }

      if (!currentUser) {
        navigate('/admin-login', { replace: true });
        return;
      }

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists() && userDoc.data().role === 'admin') {
          const userData = {
            uid: currentUser.uid,
            email: currentUser.email,
            role: 'admin',
            name: userDoc.data().name || currentUser.displayName || '',
            salary: userDoc.data().salary || '',
            joiningDate: userDoc.data().joiningDate || '',
          };
          setUser(userData);
          if (!isAdminEmailSet) {
            setAdminCredentials((prev) => ({
              ...prev,
              email: currentUser.email,
            }));
            setIsAdminEmailSet(true);
          }
        } else {
          setError('You do not have admin privileges or user data is missing.');
          navigate('/admin-login', { replace: true });
        }
      } catch (err) {
        setError('Unable to load user data. Please try again.');
        navigate('/admin-login', { replace: true });
      }
    });
    return () => unsubscribe();
  }, [navigate, isCreatingUser, isAdminEmailSet]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      try {
        setLoading(true);
        const usersCollection = collection(db, 'users');
        const usersSnapshot = await getDocs(usersCollection);
        const userList = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(userList);
      } catch (error) {
        setError('Unable to fetch user list. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const logout = async () => {
    try {
      await auth.signOut();
      navigate('/admin-login', { replace: true });
    } catch (error) {
      setError('Unable to log out. Please try again.');
    }
  };

  const filteredUsers = useMemo(() => {
    return users.filter(user => 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const updateUser = async (id, updatedData) => {
    try {
      const userRef = doc(db, 'users', id);
      await updateDoc(userRef, updatedData);
      setUsers(users.map(user => user.id === id ? { ...user, ...updatedData } : user));
      setEditingUser(null);
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Unable to update user. Please try again.');
    }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    setIsCreatingUser(true);

    try {
      if (!adminCredentials.password) {
        setError('Please enter your admin password to create users');
        setLoading(false);
        setIsCreatingUser(false);
        return;
      }

      const currentAdminUser = auth.currentUser;
      if (!currentAdminUser) {
        throw new Error('No admin user is currently signed in.');
      }

      const adminDocRef = doc(db, 'users', currentAdminUser.uid);
      const adminDoc = await getDoc(adminDocRef);
      if (!adminDoc.exists() || adminDoc.data().role !== 'admin') {
        throw new Error('Current user does not have admin privileges.');
      }

      const userCredential = await createUserWithEmailAndPassword(auth, newUser.email, newUser.password);
      const firebaseUser = userCredential.user;
      
      await updateProfile(firebaseUser, { displayName: newUser.name });

      const newUserData = {
        email: newUser.email,
        name: newUser.name,
        role: newUser.role,
        salary: newUser.salary,
        joiningDate: newUser.joiningDate,
        createdAt: new Date().toISOString(),
      };
      
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      await setDoc(userDocRef, newUserData);

      await auth.signOut();
      
      try {
        await signInWithEmailAndPassword(auth, adminCredentials.email, adminCredentials.password);
      } catch (authError) {
        setError('Invalid admin password. Please check your password.');
        setLoading(false);
        setIsCreatingUser(false);
        navigate('/admin-login', { replace: true });
        return;
      }
      
      setUsers(prevUsers => [...prevUsers, { id: firebaseUser.uid, ...newUserData }]);
      setNewUser({ email: '', password: '', role: 'employee', name: '', salary: '', joiningDate: '' });
      setAdminCredentials((prev) => ({ ...prev, password: '' }));
      setSuccess('User created successfully!');
      setActiveTab('users');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        setError('This email is already in use. Please use a different email.');
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        setError('Invalid admin password. Please check your password.');
      } else {
        setError('Unable to create user. Please try again.');
      }
    } finally {
      setLoading(false);
      setIsCreatingUser(false);
    }
  };

  const deleteUserFromSystem = async (userId, userEmail) => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const currentAdminUser = auth.currentUser;
      if (!currentAdminUser) {
        throw new Error('No admin user is currently signed in.');
      }

      const adminDocRef = doc(db, 'users', currentAdminUser.uid);
      const adminDoc = await getDoc(adminDocRef);
      if (!adminDoc.exists() || adminDoc.data().role !== 'admin') {
        throw new Error('Current user does not have admin privileges.');
      }

      // Delete from Firestore
      const userDocRef = doc(db, 'users', userId);
      await deleteDoc(userDocRef);

      // Note: Client-side deletion of other users' auth records requires Firebase Admin SDK
      // For simplicity, we only delete Firestore document and rely on server-side cleanup if needed

      setUsers(users.filter(user => user.id !== userId));
      setSuccess('User deleted successfully');
      setTimeout(() => setSuccess(''), 3000);

    } catch (error) {
      setError('Unable to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (userId, email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(`Password reset email sent to ${email}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Unable to send password reset email. Please try again.');
    }
  };

  const startEditing = (user) => {
    setEditingUser({ ...user });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };

  const saveUser = () => {
    updateUser(editingUser.id, editingUser);
  };

  const navigationItems = [
    { id: 'users', label: 'Users', icon: Users },
    { id: 'create-user', label: 'Create User', icon: UserPlus },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white shadow-lg transition-all duration-300 flex flex-col`}>
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {sidebarOpen && (
              <h1 className="text-xl font-bold text-gray-800">Admin Panel</h1>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={`p-2 rounded-lg hover:bg-gray-100 transition-colors ${!sidebarOpen ? 'mx-auto' : ''}`}
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <nav className={`flex-1 ${sidebarOpen ? 'p-4' : 'p-2'}`}>
          <div className="space-y-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.id} className="relative group">
                  <button
                    onClick={() => setActiveTab(item.id)}
                    className={`w-full flex items-center justify-center py-3 rounded-lg transition-colors ${
                      activeTab === item.id
                        ? 'bg-blue-100 text-blue-500 border border-blue-200'
                        : 'text-gray-600 hover:bg-gray-100'
                    } ${sidebarOpen ? 'px-4' : 'px-2'}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {sidebarOpen && <span className="ml-3 font-medium">{item.label}</span>}
                  </button>
                  {!sidebarOpen && (
                    <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                      {item.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        <div className={`border-t border-gray-200 ${sidebarOpen ? 'p-4' : 'p-2'}`}>
          {sidebarOpen && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-800">{user?.name || 'Admin'}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          )}
          <div className="relative group">
            <button
              onClick={logout}
              className={`w-full flex items-center justify-center py-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors ${
                sidebarOpen ? 'px-4' : 'px-2'
              }`}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="ml-3 font-medium">Logout</span>}
            </button>
            {!sidebarOpen && (
              <div className="absolute left-16 top-1/2 transform -translate-y-1/2 bg-gray-800 text-white text-sm rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                Logout
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="p-6">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Welcome back, {user?.name || 'Admin'}
            </h2>
            <p className="text-gray-600">Manage your users</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
              <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <Check className="w-5 h-5 text-green-500 mr-2" />
              <span className="text-green-700">{success}</span>
              <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-red-700">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {loading && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center">
              <span className="text-blue-700">Loading...</span>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm">
            {activeTab === 'users' && (
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-semibold text-gray-800">User Management</h3>
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={() => setActiveTab('create-user')}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add User
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Salary</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Joining Date</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((user) => (
                          <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-4">
                              {editingUser?.id === user.id ? (
                                <input
                                  type="text"
                                  value={editingUser.name || ''}
                                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              ) : (
                                <span className="font-medium text-gray-800">{user.name || 'Not provided'}</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {editingUser?.id === user.id ? (
                                <input
                                  type="email"
                                  value={editingUser.email || ''}
                                  onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              ) : (
                                <span className="text-gray-600">{user.email || 'Not provided'}</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {editingUser?.id === user.id ? (
                                <select
                                  value={editingUser.role || 'employee'}
                                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                  <option value="employee">Employee</option>
                                  <option value="admin">Admin</option>
                                </select>
                              ) : (
                                <span className="text-gray-600">{user.role || 'Not specified'}</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {editingUser?.id === user.id ? (
                                <input
                                  type="number"
                                  value={editingUser.salary || ''}
                                  onChange={(e) => setEditingUser({ ...editingUser, salary: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              ) : (
                                <span className="text-gray-600">{user.salary || 'Not specified'}</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {editingUser?.id === user.id ? (
                                <input
                                  type="date"
                                  value={editingUser.joiningDate || ''}
                                  onChange={(e) => setEditingUser({ ...editingUser, joiningDate: e.target.value })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                              ) : (
                                <span className="text-gray-600">{user.joiningDate || 'Not specified'}</span>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              {editingUser?.id === user.id ? (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={saveUser}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-md transition-colors"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={cancelEditing}
                                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => startEditing(user)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                  >
                                    <Edit3 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => changePassword(user.id, user.email)}
                                    className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-md transition-colors"
                                  >
                                    <Key className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteUserFromSystem(user.id, user.email)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="py-4 px-4 text-center text-gray-600">
                            No users found.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'create-user' && (
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Create New User</h3>
                
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 mb-2">Admin Authentication Required</h4>
                  <p className="text-yellow-700 mb-3">Please enter your admin password to create new users:</p>
                  <input
                    type="password"
                    placeholder="Enter your admin password"
                    value={adminCredentials.password}
                    onChange={(e) => setAdminCredentials({ ...adminCredentials, password: e.target.value })}
                    className="w-full max-w-sm px-3 py-2 border border-yellow-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>

                <form onSubmit={createUser} className="max-w-md space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <input
                      type="text"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="employee">Employee</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Salary
                    </label>
                    <input
                      type="number"
                      value={newUser.salary}
                      onChange={(e) => setNewUser({ ...newUser, salary: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter salary"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Joining Date
                    </label>
                    <input
                      type="date"
                      value={newUser.joiningDate}
                      onChange={(e) => setNewUser({ ...newUser, joiningDate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      disabled={loading || !adminCredentials.password}
                      className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {loading ? 'Creating...' : 'Create User'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveTab('users')}
                      className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;



